import React, { useMemo, useState } from 'react';
import {
  Breakpoints,
  Spinner,
  styled,
  TextTag,
} from '@volkswagen-onehub/components-core';
import {
  ButtonAppearance,
  TextAppearance,
  TextColor,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import { Button } from '../Button';
import { CTAButton } from '../Button/CTAButton';
import { Box, TextBox } from '../Box';
import { SelectInput } from '../Input';
import { Icon } from '../Icon';

import {
  getDynamicSize,
  useViewport,
  ViewportType,
} from '../../hooks/use-viewport';

import { useStore } from '../../store';
import { useApolloClient } from '@apollo/client';
import {
  QsgV1Model,
  NavigationGuide,
  VehicleModel,
} from '../../fetch-data/graphql';
import {
  setSelectedQsgModel,
  setNotificationMessage,
  setQsgNavigation,
  setQsgContent,
  setQsgSectionMap,
} from '../../store/slices/qsg/actions';
import {
  aggregateSectionMap,
  castQSGContentToNavigation,
  getQSGContent,
  getQSGNavigation,
} from '../../fetch-data/graphql/helpers';
import { findFirstIndexableSection } from '../../utils/navigation';
import {
  normalizeSectionId,
  getQueryParams,
  normalizeQueryToParams,
  paramsToSearchString,
} from '../../utils/query-param-helpers';
import { FontWeight } from '../../utils/typography';
import { enableDynamicSectionLoad } from '../../store/load-initial-data';
import { useHistory } from '../../hooks/use-history';
import { onVWBasicFilterResultLoadEvent_ModelSelector, onVWBasicFilterSearchButtonClickEvent_ModelSelector } from '../../tagging/tagging-helpers';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import {
  pushWithPathname,
  createFeatureHubHref,
} from '../../utils/navigation-url';
import { useFeatureServices } from '../../hooks/use-feature-services';

export const ModalContainer = styled.div`
  min-height: calc(100dvh - 40px);
  background-color: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ShowGuideButtonWrapper = styled.div`
  & button:hover,
  & a:hover {
    color: ${({ theme }) => theme.colors.content.buttonHover} !important;
  }
`;

export const GridContainer = styled.div`
  width: calc(100% * 20 / 22);

  @media (min-width: ${Breakpoints.b560}px) {
    width: calc(100% * 20 / 24);
  }

  @media (min-width: ${Breakpoints.b960}px) {
    width: calc(100% * 16 / 24);
  }

  @media (min-width: ${Breakpoints.b1280}px) {
    width: calc(100% * 10 / 24);
  }

  @media (min-width: ${Breakpoints.b1600}px) {
    width: calc(100% * 8 / 24);
  }
`;

interface ModelSelectorProps {
  loadSection: (sectionPath: string, sectionId: string) => void;
}

export const ModelSelector = ({ loadSection }: ModelSelectorProps) => {
  const history = useHistory();
  const client = useApolloClient();
  const trackingManager = useTrackingManager();
  const [state, dispatch] = useStore();
  const { selectedModel, availableModels } = state.qsg;
  const featureServices = useFeatureServices();
  const serverRequest = featureServices['s2:server-request'];
  const serverRequestUrl = serverRequest?.url;

  const { modelId, modelYear } = selectedModel;

  const modelYearOptions = useMemo(() => {
    const uniqueYears = Array.from(
      new Set(availableModels.map((m) => m.modelYear))
    );

    const sortedYears = uniqueYears.sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      const bothNumeric = !isNaN(numA) && !isNaN(numB);
      return bothNumeric ? numA - numB : a.localeCompare(b);
    });

    return sortedYears.map((year) => ({ label: year, value: year }));
  }, [availableModels]);

  const modelOptions = useMemo(
    () =>
      availableModels
        .filter((m) => m.modelYear === modelYear)
        .map((m) => ({ label: m.modelName, value: m.modelId })),
    [availableModels, modelYear]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const viewport = useViewport(Breakpoints.b560, Breakpoints.b1280);
  const isDesktop: boolean = viewport === ViewportType.DESKTOP;
  const isMobile: boolean = viewport === ViewportType.MOBILE;
  const isTablet: boolean = viewport === ViewportType.TABLET;

  const clearErrors = () => {
    setErrors({});
  };

  const onSelectModelYear = (value: string) => {
    const updated: Partial<VehicleModel> = {
      modelYear: value,
    };

    if (modelYear !== value) updated.modelId = '';
    dispatch(setSelectedQsgModel(updated));
  };

  const onSelectModel = (value: string) => {
    dispatch(setSelectedQsgModel({ modelId: value }));
  };

  const validateSubmit = () => {
    const newErrors: Record<string, string> = {};
    if (!modelYear) newErrors.modelYear = 'Please select a model year.';
    if (modelYear && !modelId) newErrors.model = 'Please select a model.';

    const modelExists = availableModels.find(
      (m) => m.modelId === modelId && m.modelYear === modelYear
    );

    if (modelId && !modelExists)
      newErrors.modelExists = `The Quick Start Guide is not available for all vehicles yet`;

    return newErrors;
  };

  const handleSubmit = async () => {
    onVWBasicFilterSearchButtonClickEvent_ModelSelector(trackingManager, {
      modelId,
      modelYear,
      modelName: modelOptions.find((m) => m.value === modelId)?.label || '',
    });

    onVWBasicFilterResultLoadEvent_ModelSelector(trackingManager, {
      modelId,
      modelYear,
      modelName: modelOptions.find((m) => m.value === modelId)?.label || '',
    });

    const newErrors = validateSubmit();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      dispatch(setNotificationMessage(newErrors.modelExists || ''));
      return;
    }

    setIsLoading(true);
    dispatch(setNotificationMessage(''));

    try {
      const [data] = await Promise.all([
        enableDynamicSectionLoad
          ? getQSGNavigation(modelId, modelYear, client)
          : getQSGContent(modelId, modelYear, client),
      ]);

      if (!data)
        throw Error(
          `The Quick Start Guide is not available for all vehicles yet`
        );

      let content: QsgV1Model | null = null;
      let navigation: NavigationGuide | null = null;
      let loadedSectionId: string | null = null;

      if (enableDynamicSectionLoad) {
        navigation = data as NavigationGuide;
        dispatch(setQsgNavigation(navigation));
      } else {
        content = data as QsgV1Model;
        navigation = castQSGContentToNavigation(content);
        dispatch(setQsgContent(content));
        dispatch(setQsgNavigation(navigation));

        const sectionMap = aggregateSectionMap(content);
        if (sectionMap) dispatch(setQsgSectionMap(sectionMap));
      }

      if (isDesktop) {
        const sections = navigation.sections ?? [];

        const defaultSection = findFirstIndexableSection(sections);
        if (defaultSection) {
          if (enableDynamicSectionLoad)
            await loadSection(defaultSection._path, defaultSection.sectionId);

          loadedSectionId = defaultSection.sectionId;
        }
      }

      // Navigate to guide route
      const query = getQueryParams(history);
      const params = normalizeQueryToParams(query);
      params['model-year'] = undefined;
      params.model = undefined;

      let pathname = `/${modelYear}/${modelId}`;
      if (loadedSectionId)
        pathname = `${pathname}/${normalizeSectionId(loadedSectionId)}`;

      const searchString = paramsToSearchString(params);

      // Always include pathname when pushing to prevent AEM from re-resolving
      pushWithPathname(history, pathname, searchString);
    } catch (err: any) {
      dispatch(setNotificationMessage(err.message));
    }

    setIsLoading(false);
  };

  return (
    <ModalContainer>
      <GridContainer>
        <Box
          style={(theme) => ({
            marginBottom: getDynamicSize(
              viewport,
              theme.size.static450,
              theme.size.static500,
              theme.size.static520
            ),
          })}
        >
          <TextBox
            appearance={TextAppearance.headline300}
            tag={TextTag.h1}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize32,
                fontWeight: TextWeight.light,
                lineHeight: TextLineHeight.TextLineHeight36,
              },
              [Breakpoints.b560]: {
                fontSize: TextSize.TextSize48,
                fontWeight: TextWeight.light,
                lineHeight: TextLineHeight.TextLineHeight52,
              },
              [Breakpoints.b1600]: {
                fontSize: TextSize.TextSize60,
                fontWeight: TextWeight.light,
                lineHeight: TextLineHeight.TextLineHeight68,
              },
            }}
            customStyle={(theme) => ({
              color: theme.colors.content.primary,
              letterSpacing: '1px',
              fontWeight: FontWeight.light,
            })}
          >
            <TextBox
              tag={TextTag.span}
              style={{
                [Breakpoints.default]: {
                  color: TextColor.inverted,
                  fontWeight: TextWeight.bold,
                },
              }}
              customStyle={{
                letterSpacing: 'inherit',
              }}
            >
              Quick Start{' '}
            </TextBox>
            Guides
          </TextBox>
        </Box>

        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            style={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              marginBottom: getDynamicSize(
                viewport,
                theme.size.static350,
                theme.size.static400
              ),
            })}
          >
            <Icon
              name='NewCarPrivate'
              style={(theme) => ({ color: theme.colors.content.primary })}
            />
            <TextBox
              appearance={TextAppearance.headline200}
              tag={TextTag.h2}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1280]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight28,
                },
              }}
              wrapperStyle={{
                paddingBottom: '4px',
              }}
              customStyle={(theme) => ({
                color: theme.colors.content.primary,
                marginLeft: '1rem',
                fontWeight: FontWeight.light,
              })}
            >
              Enter your vehicle's details
            </TextBox>
          </Box>

          <Box
            style={(theme) => ({
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: getDynamicSize(
                viewport,
                theme.size.static300,
                theme.size.static200
              ),
              marginBottom: getDynamicSize(viewport, theme.size.static400),
            })}
          >
            <Box
              style={{
                width: isTablet ? 'calc(100% * 8 / 20)' : '100%',
              }}
            >
              <SelectInput
                label={'Select Model Year'}
                options={modelYearOptions}
                value={modelYear}
                onChange={onSelectModelYear}
                onFocus={clearErrors}
                appearance={errors.modelYear ? 'error' : 'default'}
                message={errors.modelYear}
                inputId='modelYearSelect'
              />
            </Box>
            <Box
              style={{
                width: isTablet ? 'calc(100% * 12 / 20)' : '100%',
              }}
            >
              <SelectInput
                label={'Select Model'}
                options={modelOptions}
                value={modelId}
                onChange={onSelectModel}
                onFocus={clearErrors}
                appearance={
                  errors.model || errors.modelExists ? 'error' : 'default'
                }
                disabled={modelYear === ''}
                message={errors.model || errors.modelExists}
                inputId='modelSelect'
              />
            </Box>
          </Box>

          <ShowGuideButtonWrapper>
            <Button
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
              appearance={ButtonAppearance.Primary}
              onClick={handleSubmit}
              disabled={isLoading || !modelYear || !modelId}
            >
              {isLoading && <Spinner />}Show guide
            </Button>
          </ShowGuideButtonWrapper>

        </Box>
      </GridContainer>

      {/* Accordion with crawler-friendly links for each model */}
      {availableModels.length > 0 && (
        <Box
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 10,
          }}
        >
          <Box
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            style={{
              cursor: 'pointer',
              padding: '8px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="accordion"
          >
            <Icon
              name={isAccordionOpen ? 'ChevronUp' : 'ChevronDown'}
              style={(theme) => ({
                color: theme.colors.content.secondary,
                fontSize: '16px',
              })}
            />
          </Box>

          <Box
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              width: '100%',
              minWidth: '200px',
              maxWidth: '400px',
              maxHeight: isAccordionOpen ? '250px' : '0',
              overflow: isAccordionOpen ? 'auto' : 'hidden',
              transition: 'max-height 0.3s ease-out',
              backgroundColor: 'white',
              boxShadow: isAccordionOpen
                ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                : 'none',
              borderRadius: '4px',
              marginBottom: '8px',
            }}
          >
            <Box
              style={(theme) => ({
                padding: theme.size.static200,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.size.static100,
                alignItems: 'center',
              })}
            >
              {availableModels.map((model) => {
                const featureAppRoute = `/${model.modelYear}/${model.modelId}`;
                const href = createFeatureHubHref(
                  history,
                  featureAppRoute,
                  serverRequestUrl
                );

                return (
                  <CTAButton
                    key={`${model.modelYear}-${model.modelId}`}
                    tag='a'
                    href={href}
                    emphasis='tertiary'
                    style={{
                      textAlign: 'center',
                      width: '100%',
                      padding: '2px 4px',
                    }}
                  >
                    {model.modelYear} {model.modelName}
                  </CTAButton>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </ModalContainer>
  );
};
